// frontend/src/components/NotaComentario.js
import React, { useState, useEffect, useRef } from 'react';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

/**
 * NotaComentario - Componente estilo "sticky note" para mostrar/editar observaciones o comentarios.
 *
 * Props:
 *  - valor: string con el texto actual
 *  - onGuardar: (nuevoValor) => Promise<void> | void - callback al guardar
 *  - label: texto del título (ej: "Observación", "Comentarios")
 *  - color: color de la nota ('yellow' | 'blue' | 'green' | 'pink') default 'yellow'
 *  - readOnly: boolean - si es true, no se puede editar (default false)
 */
const colores = {
    yellow: {
        bg: '#fff9c4',
        bgHover: '#fff59d',
        border: '#f9a825',
        header: '#f57f17',
        shadow: 'rgba(249, 168, 37, 0.3)',
        foldBg: '#ffe082',
    },
    blue: {
        bg: '#e3f2fd',
        bgHover: '#bbdefb',
        border: '#42a5f5',
        header: '#1565c0',
        shadow: 'rgba(66, 165, 245, 0.3)',
        foldBg: '#90caf9',
    },
    green: {
        bg: '#e8f5e9',
        bgHover: '#c8e6c9',
        border: '#66bb6a',
        header: '#2e7d32',
        shadow: 'rgba(102, 187, 106, 0.3)',
        foldBg: '#a5d6a7',
    },
    pink: {
        bg: '#fce4ec',
        bgHover: '#f8bbd0',
        border: '#ec407a',
        header: '#c2185b',
        shadow: 'rgba(236, 64, 122, 0.3)',
        foldBg: '#f48fb1',
    },
};

/**
 * etiquetaEdicion: texto opcional que se agrega al encabezado automático al editar.
 * Ejemplo: 'Motivo de no inicio ART' → [DD/MM/YYYY HH:mm - Usuario] Motivo de no inicio ART:
 * Si no se pasa, el encabezado es el estándar: [DD/MM/YYYY HH:mm - Usuario]:
 */
function NotaComentario({ valor = '', onGuardar, label = 'Observación', color = 'yellow', readOnly = false, nombreUsuario = '', etiquetaEdicion = '' }) {
    const [expandido, setExpandido] = useState(false);
    const [editando, setEditando] = useState(false);
    const [textoLocal, setTextoLocal] = useState(valor);
    const [guardando, setGuardando] = useState(false);
    const textareaRef = useRef(null);
    const tema = colores[color] || colores.yellow;

    /**
     * Genera el encabezado automático con fecha/hora y nombre del usuario.
     * Formato estándar:  [DD/MM/YYYY HH:mm - NombreUsuario]:
     * Con etiqueta:      [DD/MM/YYYY HH:mm - NombreUsuario] EtiquetaEdicion:
     */
    const generarEncabezado = () => {
        const ahora = new Date();
        const dd = String(ahora.getDate()).padStart(2, '0');
        const mm = String(ahora.getMonth() + 1).padStart(2, '0');
        const yyyy = ahora.getFullYear();
        const hh = String(ahora.getHours()).padStart(2, '0');
        const min = String(ahora.getMinutes()).padStart(2, '0');
        const fecha = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
        const usuario = nombreUsuario || 'Usuario';
        if (etiquetaEdicion) {
            return `[${fecha} - ${usuario}] ${etiquetaEdicion}: `;
        }
        return `[${fecha} - ${usuario}]: `;
    };

    useEffect(() => {
        setTextoLocal(valor);
    }, [valor]);

    useEffect(() => {
        if (editando && textareaRef.current) {
            textareaRef.current.focus();
            // Poner el cursor al final
            const len = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(len, len);
        }
    }, [editando]);

    const handleToggle = () => {
        if (!editando) {
            setExpandido(!expandido);
        }
    };

    const handleEditar = (e) => {
        e.stopPropagation();
        const encabezado = generarEncabezado();
        const separador = textoLocal.trim() ? '\n' : '';
        setTextoLocal(prev => prev.trim() + separador + encabezado);
        setExpandido(true);
        setEditando(true);
    };

    const handleCancelar = (e) => {
        e.stopPropagation();
        setTextoLocal(valor);
        setEditando(false);
    };

    const handleGuardar = async (e) => {
        e.stopPropagation();
        if (onGuardar) {
            setGuardando(true);
            try {
                await onGuardar(textoLocal);
                setEditando(false);
            } catch (err) {
                console.error('Error al guardar nota:', err);
            } finally {
                setGuardando(false);
            }
        }
    };

    const textoPreview = valor
        ? (valor.length > 60 ? valor.substring(0, 60) + '...' : valor)
        : 'Sin notas — click para agregar';

    return (
        <div
            onClick={handleToggle}
            style={{
                position: 'relative',
                background: expandido ? tema.bg : tema.bg,
                border: `1px solid ${tema.border}`,
                borderRadius: '4px 4px 4px 16px',
                padding: expandido ? '16px' : '10px 16px',
                marginBottom: '16px',
                cursor: editando ? 'default' : 'pointer',
                boxShadow: expandido
                    ? `4px 4px 12px ${tema.shadow}, 0 2px 4px rgba(0,0,0,0.1)`
                    : `2px 2px 6px ${tema.shadow}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: expandido ? 'rotate(-0.3deg)' : 'rotate(-0.5deg)',
                maxWidth: expandido ? '100%' : '100%',
                minHeight: expandido ? '120px' : 'auto',
                fontFamily: "'Segoe UI', 'Patrick Hand', cursive, sans-serif",
                overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
                if (!expandido) {
                    e.currentTarget.style.background = tema.bgHover;
                    e.currentTarget.style.transform = 'rotate(0deg) translateY(-2px)';
                    e.currentTarget.style.boxShadow = `4px 4px 12px ${tema.shadow}, 0 4px 8px rgba(0,0,0,0.15)`;
                }
            }}
            onMouseLeave={(e) => {
                if (!expandido) {
                    e.currentTarget.style.background = tema.bg;
                    e.currentTarget.style.transform = 'rotate(-0.5deg)';
                    e.currentTarget.style.boxShadow = `2px 2px 6px ${tema.shadow}`;
                }
            }}
        >
            {/* Efecto de esquina doblada */}
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: expandido ? '0 28px 28px 0' : '0 20px 20px 0',
                borderColor: `transparent ${tema.foldBg} transparent transparent`,
                filter: 'drop-shadow(-1px 1px 1px rgba(0,0,0,0.1))',
                transition: 'all 0.3s ease',
                zIndex: 2,
            }} />
            {/* Triángulo blanco para simular el doblez */}
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: expandido ? '0 28px 28px 0' : '0 20px 20px 0',
                borderColor: `${tema.foldBg} transparent transparent transparent`,
                zIndex: 3,
            }} />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: expandido ? '10px' : '0',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <StickyNote2Icon style={{
                        color: tema.header,
                        fontSize: expandido ? '22px' : '18px',
                        transition: 'font-size 0.3s ease',
                    }} />
                    <span style={{
                        fontWeight: 700,
                        color: tema.header,
                        fontSize: expandido ? '1rem' : '0.9rem',
                        letterSpacing: '0.02em',
                        transition: 'font-size 0.3s ease',
                    }}>
                        📌 {label}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {!readOnly && !editando && expandido && (
                        <button
                            onClick={handleEditar}
                            title="Editar"
                            style={{
                                background: 'none',
                                border: `1px solid ${tema.border}`,
                                borderRadius: '8px',
                                padding: '4px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: tema.header,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = tema.bgHover;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                            }}
                        >
                            <EditIcon style={{ fontSize: '16px' }} />
                            Editar
                        </button>
                    )}

                    {editando && (
                        <>
                            <button
                                onClick={handleGuardar}
                                disabled={guardando}
                                title="Guardar"
                                style={{
                                    background: tema.header,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '4px 12px',
                                    cursor: guardando ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    opacity: guardando ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                }}
                            >
                                <SaveIcon style={{ fontSize: '16px' }} />
                                {guardando ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button
                                onClick={handleCancelar}
                                title="Cancelar"
                                style={{
                                    background: 'none',
                                    border: `1px solid ${tema.border}`,
                                    borderRadius: '8px',
                                    padding: '4px 10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: tema.header,
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    transition: 'all 0.2s',
                                }}
                            >
                                <CloseIcon style={{ fontSize: '16px' }} />
                                Cancelar
                            </button>
                        </>
                    )}

                    {/* Indicador de expandir/colapsar */}
                    {!editando && (
                        <span style={{
                            color: tema.header,
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginLeft: '4px',
                            transition: 'transform 0.3s ease',
                            display: 'inline-block',
                            transform: expandido ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}>
                            ▼
                        </span>
                    )}
                </div>
            </div>

            {/* Preview (colapsado) */}
            {!expandido && (
                <div style={{
                    color: '#5d4037',
                    fontSize: '0.85rem',
                    fontStyle: valor ? 'normal' : 'italic',
                    opacity: valor ? 0.85 : 0.55,
                    marginTop: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {textoPreview}
                </div>
            )}

            {/* Contenido expandido */}
            {expandido && (
                <div style={{
                    marginTop: '4px',
                    animation: 'fadeIn 0.3s ease-in',
                }}>
                    {editando ? (
                        <textarea
                            ref={textareaRef}
                            value={textoLocal}
                            onChange={(e) => setTextoLocal(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Escribí tu nota o comentario aquí..."
                            rows={4}
                            style={{
                                width: '100%',
                                border: `1.5px dashed ${tema.border}`,
                                borderRadius: '8px',
                                padding: '10px 12px',
                                fontSize: '0.95rem',
                                fontFamily: "'Segoe UI', sans-serif",
                                background: 'rgba(255,255,255,0.6)',
                                color: '#3e2723',
                                resize: 'vertical',
                                outline: 'none',
                                lineHeight: '1.6',
                                minHeight: '80px',
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = tema.header;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = tema.border;
                            }}
                        />
                    ) : (
                        <div style={{
                            color: '#3e2723',
                            fontSize: '0.95rem',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontStyle: valor ? 'normal' : 'italic',
                            opacity: valor ? 1 : 0.5,
                            padding: '4px 0',
                        }}>
                            {valor || 'Sin notas aún. Hacé click en "Editar" para agregar una.'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default NotaComentario;